/**
 * Formate un date string selon le pattern fourni.
 * NOTE: Pour les colonnes avec pattern fixe, préférer Component.formatDateValue() qui est optimisé
 */
export function datePatternLang(
  date: string | Date,
  pattern?: string | null,
  regex?: RegExp,
): string {
  // Optimisation: Si c'est une string qui commence par "date:", extraire directement l'ISO
  if (typeof date === "string" && date.startsWith("date:")) {
    // Format: "date:YYYY-MM-DDTHH:mm:ss:pattern" ou "date:YYYY-MM-DDTHH:mm:ss.SSS:pattern"
    // La partie ISO commence à l'index 5 et fait 19 ou 23 caractères (avec millisecondes)
    const isoStart = 5;
    const firstColon = date.indexOf(":", isoStart + 19); // Chercher le ":" après la date ISO

    if (firstColon !== -1) {
      const isoDate = date.substring(isoStart, firstColon);
      const dateExec = new Date(isoDate);

      // Si un pattern est fourni, l'utiliser (c'est le pattern de la colonne)
      // Sinon, extraire le pattern de la string
      const finalPattern = pattern || date.substring(firstColon + 1);
      return formatDateWithPattern(dateExec, finalPattern);
    }
  }

  if (!regex) {
    regex
      = /[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]+)?([Zz]|([/+-])([01]\d|2[0-3]):?([0-5]\d)?)?/i;
  }
  const dateString = typeof date === "string" ? date : date.toISOString();
  const match = regex.exec(dateString);
  // soit un objet Date soit une string préexistante
  const dateExec: Date
    = match && match[0] ? new Date(match[0]) : typeof date === "string" ? new Date(date) : date;

  return formatDateWithPattern(dateExec, pattern);
}

/**
 * Formate une Date selon un pattern donné (optimisé sans toLocaleString)
 */
function formatDateWithPattern(dateExec: Date, pattern?: string | null): string {
  if (pattern != null) {
    let result = pattern;

    // Extraire les valeurs une seule fois
    const year = dateExec.getFullYear();
    const month = dateExec.getMonth() + 1;
    const day = dateExec.getDate();
    const hours = dateExec.getHours();
    const minutes = dateExec.getMinutes();
    const seconds = dateExec.getSeconds();

    // Remplacer les patterns (optimisé : pas de toLocaleString)
    if (result.includes("yyyy")) {
      result = result.replace("yyyy", String(year));
    }
    if (result.includes("MM")) {
      result = result.replace("MM", String(month).padStart(2, "0"));
    }
    else if (result.includes("M")) {
      result = result.replace("M", String(month));
    }
    if (result.includes("dd")) {
      result = result.replace("dd", String(day).padStart(2, "0"));
    }
    else if (result.includes("d")) {
      result = result.replace("d", String(day));
    }
    if (result.includes("HH")) {
      result = result.replace("HH", String(hours).padStart(2, "0"));
    }
    if (result.includes("mm")) {
      result = result.replace("mm", String(minutes).padStart(2, "0"));
    }
    if (result.includes("ss")) {
      result = result.replace("ss", String(seconds).padStart(2, "0"));
    }

    return result;
  }
  else {
    const lang = localStorage.getItem("lang") || undefined;
    return dateExec.toLocaleDateString(lang);
  }
}

/**
 * Formate un objet Date selon le pattern fourni.
 */
export function datePatternFormated(date: Date, pattern?: string): string {
  if (pattern != null && date != null) {
    if (pattern.includes("mm") || pattern.includes("HH") || pattern.includes("ss")) {
      const hour = date.toLocaleString("en-US", { hour: "2-digit", hour12: false });
      const minute = (date.getMinutes() < 10 ? "0" : "") + date.getMinutes();
      const second = (date.getSeconds() < 10 ? "0" : "") + date.getSeconds();
      pattern = pattern.replace("HH", hour);
      pattern = pattern.replace("mm", minute);
      pattern = pattern.replace("ss", second);
    }
    const year = date.toLocaleString("default", { year: "numeric" });
    pattern = pattern.replace("yyyy", year);
    if (pattern.includes("MM") || pattern.includes("dd")) {
      const month = date.toLocaleString("default", { month: "2-digit" });
      pattern = pattern.replace("MM", month);
    }
    else {
      const month = date.toLocaleString("default", { month: "numeric" });
      pattern = pattern.replace("M", month);
    }
    if (pattern.includes("dd")) {
      const day = date.toLocaleString("default", { day: "2-digit" });
      pattern = pattern.replace("dd", day);
    }
    else {
      const day = date.toLocaleString("default", { day: "numeric" });
      pattern = pattern.replace("d", day);
    }
    return pattern;
  }
  else {
    const lang = localStorage.getItem("lang") || undefined;
    return new Date(date).toLocaleDateString(lang);
  }
}

/**
 * Prend une date string ou un tableau nombre et retourne un Date ou le string formaté selon la locale.
 */
export function formatedDateArray(dateToFormat: string | number[] | Date | undefined | null): string {
  if (dateToFormat === undefined || dateToFormat === null || (Array.isArray(dateToFormat) && dateToFormat.length === 0)) {
    return "";
  }
  let localDate: Date | undefined;
  if (dateToFormat instanceof Date) {
    localDate = dateToFormat;
  }
  else if (typeof dateToFormat === "string") {
    try {
      localDate = new Date(dateToFormat);
      if (isNaN(localDate.getTime())) throw new Error("Invalid date string");
    }
    catch (_e) {
      return "";
    }
  }
  else if (Array.isArray(dateToFormat) && dateToFormat.length !== 0) {
    localDate = new Date(
      dateToFormat[0],
      dateToFormat[1] - 1,
      dateToFormat[2],
      dateToFormat[3],
      dateToFormat[4],
      dateToFormat[5],
    );
  }
  if (localDate) {
    const lang = localStorage.getItem("lang") || navigator.language;
    return new Intl.DateTimeFormat(lang, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(localDate);
  }
  return "";
}

/**
 * Cherche un pattern de date utilisé pour un champ référence donné.
 */
export function patternCheckerDateRef(
  application: any,
  columnName: string,
  refId: string,
): string | undefined {
  if (
    application.configuration.references
    && Object.keys(application.configuration.dataDescription[refId].validations).length !== 0
  ) {
    const validationsKeys = Object.keys(application.configuration.dataDescription[refId].validations);
    for (const item of validationsKeys) {
      const columns = application.configuration.references[refId].validations[item]?.columns;
      for (const column in columns) {
        if (columns[column] === columnName) {
          if (
            application.configuration.references[refId].validations[item].checker?.params?.pattern
          ) {
            return application.configuration.references[refId].validations[item].checker.params.pattern;
          }
        }
      }
    }
  }
  else if (
    application.configuration.dataDescription?.[refId].componentDescriptions[columnName]?.checker?.pattern
  ) {
    return application.configuration.dataDescription[refId].componentDescriptions[columnName].checker.pattern;
  }
}

/**
 * Parse une string de date selon un format attendu (ex: dd/MM/yyyy, etc...).
 * @returns Date
 */
export function parseDate(input: string, format = "dd/MM/yyyy"): Date {
  const parts = input.match(/(\d+)/g);
  let i = 0;
  const fmt: Record<string, number> = {};
  format.replace(/(yyyy|dd|MM|hh|mm|ss)/g, function (part) {
    fmt[part] = i++;
    return "";
  });

  if (!parts) {
    return new Date(""); // Date invalide
  }

  if (format.includes("hh") && format.includes("mm") && format.includes("ss")) {
    return new Date(
      +parts[fmt["yyyy"]],
      +parts[fmt["MM"]] - 1,
      +parts[fmt["dd"]],
      +parts[fmt["hh"]],
      +parts[fmt["mm"]],
      +parts[fmt["ss"]],
    );
  }
  else if (format.includes("hh") && format.includes("mm")) {
    return new Date(
      +parts[fmt["yyyy"]],
      +parts[fmt["MM"]] - 1,
      +parts[fmt["dd"]],
      +parts[fmt["hh"]],
      +parts[fmt["mm"]],
      0,
    );
  }
  else if (format.includes("hh")) {
    return new Date(
      +parts[fmt["yyyy"]],
      +parts[fmt["MM"]] - 1,
      +parts[fmt["dd"]],
      +parts[fmt["hh"]],
      0,
      0,
    );
  }
  else {
    return new Date(+parts[fmt["yyyy"]], +parts[fmt["MM"]] - 1, +parts[fmt["dd"]], 0, 0, 0);
  }
}
